import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsCustomerMode } from '@plato/app-core/hooks/use-customer-mode';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { FromNow } from '@plato/app-shared/components/from-now';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { DateText } from '@plato/ui-legacy/components/common/date-time-text';
import { DoubleDeckerCell } from '@plato/ui-legacy/components/list-views/double-decker-cell';
import { IdAndNameCell } from '@plato/ui-legacy/components/list-views/id-and-name-cell';
import { MarginPercentage } from '@plato/ui-legacy/components/list-views/margin-percentage';
import { DataText } from '@plato/ui-legacy/components/typography/data-text';
import { QuantityText } from '@plato/ui-legacy/components/typography/quantity-text';
import type { ColumnDef } from '@plato/ui/components/data-table';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from '@plato/ui/components/info-hint';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetManyQuotesOrdersListContaingProductListItem } from '../../../server/types/infer-trpc-types.ts';
import { OrderLink } from '../../quote-order-detail/components/quote-order-link.tsx';
import { OrderStatusBadge } from '../../statuses/components/order-status-badge.tsx';

export function useOrderColumnsForProductDetail() {
    const { t } = useTranslation();

    const { canReadGrossMargin } = useAppConfig();
    const customerViewToggled = useIsCustomerMode();

    const orderColumns: ColumnDef<GetManyQuotesOrdersListContaingProductListItem>[] = useMemo(
        () => [
            {
                accessorKey: 'customer_name',
                header: t('table_header__customer_id', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row, cell }) => (
                    <IdAndNameCell
                        to={`/customers/${encodeURIComponent(row.original.customer_id ?? '')}`}
                        id={row.original.customer_id}
                        title={cell.renderValue() as string}
                    />
                ),
                size: 250,
                meta: { pinned: 'left' },
            },
            {
                accessorKey: 'order_id',
                header: t('table_header__order_id', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => <OrderLink transactionId={row.original.order_id} />,
            },
            {
                accessorKey: 'status',
                header: t('table_header__status', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <OrderStatusBadge status={row.original.status} type={SrSalesOrderType.ORDER} />
                ),
            },
            {
                accessorKey: 'entry_date',
                header: t('table_header__order_created', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DoubleDeckerCell
                        align="left"
                        topClassName="text-neutral-500"
                        topLevel={<FromNow>{row.original.entry_date}</FromNow>}
                        bottomLevel={<DateText date={row.original.entry_date} />}
                    />
                ),
            },
            {
                accessorKey: 'item_quantity_in_order',
                header: t('table_header__quantity', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <QuantityText
                            quantity={row.original.item_quantity_in_order}
                            unit={row.original.quantity_unit_sales}
                        />
                    </DataText>
                ),
            },

            {
                accessorKey: 'product_unit_price',
                header: t('table_header__price', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty decimals={2}>{row.original.product_unit_price}</CurrencyOrEmpty>
                    </DataText>
                ),
            },
            {
                accessorKey: 'product_total_in_order',
                header: () => (
                    <span className="flex items-center gap-tight">
                        {t('table_header__total', { ns: 'quotes-orders-list' })}
                        <InfoHint>
                            <InfoHintTrigger />
                            <InfoHintContent>
                                <InfoHintHeader>
                                    <InfoHintTitle>
                                        {t('table_header__total', { ns: 'quotes-orders-list' })}
                                    </InfoHintTitle>
                                </InfoHintHeader>
                                <InfoHintBody>
                                    {t('table_header__total_tooltip', { ns: 'product-details' })}
                                </InfoHintBody>
                            </InfoHintContent>
                        </InfoHint>
                    </span>
                ),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty decimals={2}>{row.original.product_total_in_order}</CurrencyOrEmpty>
                    </DataText>
                ),
            },
            {
                accessorKey: 'product_gross_margin_in_order',
                meta: {
                    enabled: !customerViewToggled && canReadGrossMargin,
                    aiChat: {
                        requires: ['canReadGrossMargin'],
                    },
                },
                header: t('table_header__margin', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty decimals={2}>
                            {row.original.product_gross_margin_in_order}
                        </CurrencyOrEmpty>
                    </DataText>
                ),
            },
            {
                accessorKey: 'product_gross_margin_percent',
                meta: {
                    enabled: !customerViewToggled && canReadGrossMargin,
                    aiChat: {
                        description: 'Gross margin percentage of this product within the order',
                        requires: ['canReadGrossMargin'],
                    },
                },
                header: t('table_header__margin_percent', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <MarginPercentage
                            margin={row.original.product_gross_margin_in_order}
                            revenue={row.original.product_total_in_order}
                        />
                    </DataText>
                ),
            },
        ],
        [t, canReadGrossMargin, customerViewToggled],
    );

    return orderColumns;
}
